/**
 * Team-planet tuning. Pure helpers that consume these live in
 * `packages/game-engine/src/teamPlanetEngine.ts`, mirroring the
 * `constants/alliances.ts` / `game-engine/src/alliance.ts` split.
 *
 * A Team planet is an EXISTING player `Planet` document, converted in
 * place (`/convertToTeamPlanet`) — never a newly-created document, and
 * everything about it (buildings, resources, ships, queue) resets to empty
 * on conversion. See `Planet.allianceId`'s doc comment
 * (`packages/shared/src/types/planet.types.ts`).
 */
/**
 * Minimum {@link ALLIANCE_BUILDING} (`constants/alliances.ts`) level, on ANY
 * of the leader's planets, to convert one of their planets into the Team
 * planet — deliberately higher than `ALLIANCE_REQUIRED_BUILDING_LEVEL` (1),
 * the prerequisite for founding the alliance itself. The planet being
 * CONVERTED does not itself need this level — only "on any planet", same
 * "any owned planet qualifies" rule alliance founding already uses.
 */
export const TEAM_PLANET_REQUIRED_BUILDING_LEVEL = 10;
/**
 * Every building's production/storage benefit is this many times a normal
 * planet's, at the same level. Building COSTS are never scaled: a Team
 * planet pays the normal price (with its own Architecture discount) for 3x
 * the income (2026-09-29 ruling, superseding the 2026-08-14 "3x more
 * expensive with 3x more benefits" one). Deliberately NOT applied to:
 *   - Ship-build TIME reduction from Factory/Automation Center — these
 *     already cap at 75%/25% respectively on a normal planet
 *     (`SHIPYARD_TIME_REDUCTION_CAP`/`AUTOMATION_CENTER_TIME_REDUCTION_CAP`,
 *     `constants/ships.ts`), and the Team planet uses those SAME caps
 *     unmodified rather than tripling them.
 *   - The Engineering Facility/Automation Center "robot count" production bonus
 *     (issue #56, `calculateTotalRobots`/`distributeRobotsEvenly`) — it
 *     flows through the normal (1x) formula and only THEN gets the flat
 *     ×3 applied once, on top, like every other building's output; it is
 *     never separately re-multiplied.
 *   - Building construction TIME (`computeBuildDurationSeconds`) — only
 *     ongoing benefit is 3x'd, not how long an upgrade takes.
 *   - Ship-build resource COST — not stated to change; ships cost the
 *     normal (1x) amount even though the shipyard itself is shared.
 */
export const TEAM_PLANET_ECONOMY_MULTIPLIER = 3;
/**
 * Fixed population capacity, replacing the normal Launcher-derived formula
 * entirely — "9x the normal population (261,900) instead of 3x" (i.e. NOT
 * simply the normal Launcher formula's result ×3; the 261,900 constant
 * — shared with `SUN_SUPPORT_MAX_POPULATION`, deliberately the same
 * round number — applies flat regardless of Launcher level).
 */
export const TEAM_PLANET_POPULATION_CAPACITY = 261900;
/** Ozone never affects a Team planet (docs/game-details/resources.md, pre-existing note) — ticks always treat it as fully recovered, never locked. */
export const TEAM_PLANET_OZONE_EXEMPT = true;
/** Concurrent building-upgrade slots — a shared, alliance-funded queue, between a Normal and Platinum player's own tiers (`constants/queue.ts`). */
export const TEAM_PLANET_BUILDING_QUEUE_LIMIT = 5;
/** Concurrent science-research slots, same reasoning as the building queue. */
export const TEAM_PLANET_SCIENCE_QUEUE_LIMIT = 3;
/**
 * The shipyard is THREE independent FIFO queues, one per race — never a
 * single shared line — so Humans/Robots/Axons ship-build orders never
 * block each other. A player may only queue into their OWN race's lane
 * (2026-08-14 gameplay ruling); the leader may cancel any pending item in
 * ANY lane, everyone else only their own.
 */
export const TEAM_PLANET_SHIP_BUILD_LANES = ["humans", "robots", "axons"];
/**
 * Concurrent ship-upgrade slots PER RACE LANE — the Team planet's Tech Center
 * runs three independent FIFOs (one per race), each capped at the normal
 * player tier's limit (Team planets have no premium tier).
 */
export const TEAM_PLANET_SHIP_UPGRADE_QUEUE_LIMIT = 3;
export const TEAM_PLANET_CANCEL_SHIP_UPGRADE_NOT_OWN_MESSAGE = "You may only cancel your own queued upgrades";
export const TEAM_PLANET_CANCEL_SHIP_UPGRADE_HIGHER_LEVEL_MESSAGE = "Another member has queued a higher level of this ship — only the leader can cancel this upgrade";
//# sourceMappingURL=teamPlanets.js.map